import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  inject,
  ViewChild,
} from '@angular/core';
import { MatSidenav } from '@angular/material/sidenav';
import { CartItemFacade } from '@features/cart-item/facades/cart-item-facade/cart-item.facade';
import { PointsAccountFacade } from '@features/points-account/facades/points-account/points-account.facade';
import { PointsSummaryFacade } from '@features/points-summary/facades/points-summary-facade/points-summary.facade';
import { UserPreferenceFacade } from '@features/user-preference/facades/user-preference/user-preference.facade';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { getAuthIsLoggedIn } from '@rd/core/auth/store/selectors/auth.selectors';
import { LoyaltyCurrencyFacade } from '@rd/core/facades/loyalty-currency-facade/loyalty-currency.facade';
import { DomService } from '@rd/core/services/dom/dom.service';
import { GuardhouseService } from '@rd/core/services/guardhouse/guardhouse.service';
import { UrlUtilsService } from '@rd/core/services/url-utils/url-utils.service';
import { CORE_AUTH_SECURE_ROUTE_DIALOG } from '@rd/core/tokens/popup.token';
import { DialogService } from '@rd/popup/services/dialog/dialog.service';
import {
  GuardHouseLogoutCodes,
  LoyaltyCurrency,
  PointsAccount,
  PointsSummary,
  UserPreference,
} from '@rd/types';
import { isNotNull } from '@rd/utils/isNotNull';
import * as CSS from 'csstype';
import { combineLatest, Observable, Subject } from 'rxjs';
import { filter, map, switchMapTo, tap } from 'rxjs/operators';
import { RewardsProgramSchemes } from '../../../rewards-program/types';
import { RewardsProgramsViewFacade } from '../../../rewards-program/views/facade/rewards-program-view.facade';
import { BnzNavLinksService } from '../../services/bnz-nav-links.service';
import { BNZ_STAR_ICON_ENABLED } from '../../tokens/features.tokens';

type UserAction = 'login' | 'logout' | 'enroll';

@UntilDestroy()
@Component({
  selector: 'bnz-mobile-header',
  templateUrl: './mobile-header.component.html',
  styleUrls: ['./mobile-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BnzMobileHeaderComponent {
  @HostBinding('class.rd-bnz-mobile-header') componentClass = true;

  @ViewChild('sidenav') sideNavRef: MatSidenav;
  sideNavOpenEvent: Subject<void> = new Subject<void>();

  isStarIconEnabled = inject(BNZ_STAR_ICON_ENABLED);

  isSideNavOpen = false;
  isLoggedIn$: Observable<boolean> = this.store
    .select(getAuthIsLoggedIn)
    .pipe(filter(isNotNull));

  loyaltyCurrency$: Observable<LoyaltyCurrency> =
    this.loyaltyCurrencyFacade.getLoyaltyCurrencySelected$.pipe(
      filter(isNotNull),
    );

  pointsSummary$: Observable<PointsSummary> =
    this.pointsSummaryFacade.pointsSummary$.pipe(filter(isNotNull));

  pointsInformation$: Observable<[PointsSummary, LoyaltyCurrency]> =
    combineLatest([this.pointsSummary$, this.loyaltyCurrency$]).pipe(
      filter((values) => values.some(isNotNull)),
    );

  pointsAccountId$: Observable<string | undefined> =
    this.userPreferenceFacade.userPreferences$.pipe(
      map((userPreference: UserPreference) => userPreference.pointsAccountId),
    );

  activePointsAccount$: Observable<PointsAccount | undefined> = combineLatest([
    this.pointsAccountFacade.pointsAccounts$,
    this.pointsAccountId$,
  ]).pipe(
    map(([pointsAccounts, id]) =>
      pointsAccounts.find((account) => account.id === id),
    ),
  );

  isPointsNavShown$ = combineLatest([
    this.activePointsAccount$,
    this.pointsSummaryFacade.pointsSummary$,
    this.isLoggedIn$,
  ]).pipe(map((values) => values.reduce((acc, val) => acc && !!val, true)));

  isFlybuys$: Observable<boolean> =
    this.rewardsProgramsViewFacade.selectedScheme$.pipe(
      map((scheme) => scheme === RewardsProgramSchemes.Flybuys),
    );

  private coreAuthSecureRouteDialogComponentType = inject(
    CORE_AUTH_SECURE_ROUTE_DIALOG,
  );

  constructor(
    public bnzNavLinks: BnzNavLinksService,
    public cartItemFacade: CartItemFacade,
    private urlUtilsService: UrlUtilsService,
    private domService: DomService,
    private guardhouseService: GuardhouseService,
    private store: Store,
    public pointsAccountFacade: PointsAccountFacade,
    public pointsSummaryFacade: PointsSummaryFacade,
    private userPreferenceFacade: UserPreferenceFacade,
    private loyaltyCurrencyFacade: LoyaltyCurrencyFacade,
    private dialogService: DialogService,
    private translateService: TranslateService,
    private rewardsProgramsViewFacade: RewardsProgramsViewFacade,
  ) {}

  get logoImageUrl(): string {
    return this.urlUtilsService.imageUrl('/images/branding/bnz.png');
  }

  openSideNav(): void {
    const openSideNavActions = () => {
      this.isSideNavOpen = true;
      this.sideNavRef.open();
      this.sideNavOpenEvent.next();
    };

    this.isLoggedIn$
      .pipe(
        untilDestroyed(this),
        tap((isLoggedIn) => {
          if (!isLoggedIn) {
            openSideNavActions();
          }
        }),
        filter(Boolean),
        switchMapTo(this.isFlybuys$),
        map((isFlybuys) => !isFlybuys),
        filter(Boolean),
        tap(openSideNavActions),
      )
      .subscribe();
  }

  closeSideNav(): void {
    this.isSideNavOpen = false;
    this.sideNavRef.close();
  }

  handleSideNavOpen(): void {
    this.domService.lockBody();
  }

  handleSideNavClose(): void {
    this.domService.unlockBody();
  }

  style(): CSS.Properties {
    if (!this.isSideNavOpen) {
      return {
        pointerEvents: 'none',
      };
    }
    return {};
  }

  handleUserAction(action: UserAction): void {
    switch (action) {
      case 'login':
        this.openLoginModal();
        break;

      case 'logout':
        this.guardhouseService.redirectToLogoutUrl(
          GuardHouseLogoutCodes.IntentionalLogout,
        );
        break;
    }
  }

  openLoginModal(): void {
    this.dialogService.open(this.coreAuthSecureRouteDialogComponentType, {
      id: 'auth-secure-route-dialog',
      screenMode: 'center-md',
      ariaLabel: this.translateService.instant(
        'accessibility.core.auth_dialog_aria_label',
      ),
    });
  }
}
